import { data, useActionData } from 'react-router'
import { BookDemoTabbedForm } from '#app/components/book-demo-tabbed-form.tsx'
import {
	FeatureGrid,
	MarketingButton,
	MarketingHero,
	MarketingPageShell,
	MarketingSection,
	type MarketingFeature,
} from '#app/components/marketing-page-shell.tsx'
import { automotiveDealerWebsiteSchema } from '#app/utils/constants.ts'
import { processSalesLeadSubmission } from '#app/utils/marketing-form.server.ts'
import { type Route } from './+types/automotive-dealers.ts'

const MetaTitle = `Automotive Dealer Websites | Mobile-First Sites Built to Sell Cars`
const MetaDescription = `Fast, mobile-first websites purpose-built for car dealerships. Inventory sync, local SEO, and lead capture tools designed to turn browsers into buyers.`

const directScheduleNamespace = 'website-demo-direct'

export const handle = { chrome: 'payload' }

const features: MarketingFeature[] = [
	{
		label: '01',
		title: 'Mobile-first by default',
		description:
			'Most car shoppers start their search on a phone. DealerProEdge websites are built mobile-first, so browsing inventory, requesting a quote, or applying for financing feels effortless on any device.',
	},
	{
		label: '02',
		title: 'Speed that protects conversions',
		description:
			'Every additional second of load time gives shoppers a reason to leave for a competitor. Our platform is engineered around Core Web Vitals so pages load fast and stay fast as inventory grows.',
	},
	{
		label: '03',
		title: 'Inventory that stays in sync',
		description:
			'Multi-location inventory sync keeps SRPs and VDPs accurate the moment a vehicle arrives, sells, or changes price, across every rooftop in your group.',
	},
	{
		label: '04',
		title: 'Local SEO fundamentals included',
		description:
			'Schema markup, location pages, and inventory-aware content structures give your dealership a stronger starting position in local search from day one.',
	},
	{
		label: '05',
		title: 'A no-code content editor',
		description:
			'Update menus, pages, offers, and blog posts without waiting on a developer. Your team keeps the site current without slowing down.',
	},
	{
		label: '06',
		title: 'Built for the whole funnel',
		description:
			'Lead capture, click-to-call, financing pre-qualification, and secure online payments keep shoppers moving from research to showroom visit.',
	},
]

const includedFeatures = [
	'EdgeCMS Website Platform',
	'ADA-compliant responsive design',
	'Dynamic vehicle search filters',
	'Multi-location inventory sync',
	'Local SEO and structured data',
	'Lead capture and analytics dashboard',
	'Secure online payments',
	'Financing and pre-qualification tools',
]

export const meta: Route.MetaFunction = ({ matches }) => {
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path = rootData?.requestInfo?.path ?? '/industries/automotive-dealers'
	const canonicalUrl = `${origin}${path}`

	return [
		{ title: MetaTitle },
		{ name: 'description', content: MetaDescription },
		{ name: 'twitter:title', content: MetaTitle },
		{ name: 'twitter:description', content: MetaDescription },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ property: 'og:title', content: MetaTitle },
		{ property: 'og:description', content: MetaDescription },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: canonicalUrl },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
		{ 'script:ld+json': automotiveDealerWebsiteSchema },
	]
}

export async function action(args: Route.ActionArgs) {
	const formData = await args.request.formData()
	const result = await processSalesLeadSubmission(
		formData,
		'website-demo',
		args,
	)

	if (!result.success) {
		return data(
			{ result: result.submission },
			{
				status: result.status,
				headers: 'headers' in result ? result.headers : undefined,
			},
		)
	}

	return data({ success: true })
}

export default function AutomotiveDealersPage() {
	const actionData = useActionData<typeof action>()
	const didSubmit = actionData && 'success' in actionData && actionData.success
	const lastResult =
		actionData && 'result' in actionData ? actionData.result : undefined

	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="Automotive Dealers"
				title="Websites built to turn car shoppers into buyers."
				subtitle="Most car buyers start their search on a phone, and a slow site loses them before they ever see your inventory. DealerProEdge gives automotive dealers a fast, mobile-first website with inventory sync, local SEO, and lead capture built in from the start."
				stats={[
					{ value: '70%+', label: 'Auto shoppers research on mobile' },
					{ value: '20%', label: 'Conversions lost per second of delay' },
					{ value: '24/7', label: 'Inventory visibility' },
				]}
				actions={
					<>
						<MarketingButton to="#getStarted">Schedule My Demo</MarketingButton>
						<MarketingButton to="/car-dealer-websites" variant="light">
							See the Platform
						</MarketingButton>
					</>
				}
			/>

			<MarketingSection
				eyebrow="The reality"
				title="Shoppers are researching on mobile before they ever call."
			>
				<div className="text-black/66 max-w-4xl text-xl leading-9">
					<p>
						Today&apos;s car buyers browse inventory, compare pricing, and start
						financing conversations from their phone, often before a salesperson
						ever hears from them. A slow or dated website does not just look
						bad, it actively sends that shopper to a competitor whose site
						loaded faster.
					</p>
				</div>
			</MarketingSection>

			<MarketingSection
				eyebrow="Website essentials"
				title="Everything your digital showroom needs to move faster."
			>
				<FeatureGrid features={features} />
			</MarketingSection>

			<MarketingSection
				eyebrow="Included"
				title="One performance foundation, every plan."
			>
				<div className="grid grid-cols-1 border-l border-t border-black md:grid-cols-2">
					{includedFeatures.map((item) => (
						<div
							className="border-b border-r border-black bg-[#fffffa]/60 p-7"
							key={item}
						>
							<p className="text-black/72 text-base leading-7">• {item}</p>
						</div>
					))}
				</div>
			</MarketingSection>

			<MarketingSection id="getStarted" className="scroll-mt-24">
				<div className="mx-auto max-w-[620px]">
					<div className="mb-8 text-center">
						<h2 className="text-4xl font-medium leading-tight text-black md:text-5xl">
							Schedule a Demo
						</h2>
						<p className="text-black/58 mx-auto mt-4 max-w-[520px] text-base leading-7 md:text-lg">
							Submit the form below or schedule a time directly on our calendar.
						</p>
					</div>
					<BookDemoTabbedForm
						isSubmitted={Boolean(didSubmit)}
						lastResult={lastResult}
						scheduleNamespace={directScheduleNamespace}
						successDescription="Your website demo request went to the DealerProEdge team. We will review your current site and follow up shortly."
						usePairedFieldRows
					/>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	)
}
